import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { base_colorSurfaceMutedHover } from 'ext-ui/style/colors'
import React from 'react'
import { useDispatch } from 'react-redux'
import AttachmentVerificationTooltip from 'src/components/AttachmentVerificationTooltip/AttachmentVerificationTooltip'
import SvgCheckNew from 'src/components/Svgs/SvgCheckNew'
import PolicyStatusTooltipContent from 'src/components/TransactionsDataGrid/cells/PolicyStatusTooltipContent'
import { useTranslate } from 'src/localization/Trns'
import {
  ReceiptVerificationStatus,
  receiptVerificationStatusToIcon,
  receiptVerificationStatusToTooltip,
} from 'src/models/receiptVerification.models'
import {
  TransactionDetailModel,
  TransactionDetailResponse,
} from 'src/models/transaction.models'
import { transactionsQueryKeys } from 'src/queries/queries.constants'
import { useRequestConfig } from 'src/queries/queries.utils'
import { createSetIsAttachmentPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface ReceiptVerificationCellProps {
  status?: ReceiptVerificationStatus
  reasons?: string[]
  transactionId?: string
  thumbnailUrl?: string
  showReceiptAgentAvatar?: boolean
}

export default function ReceiptVerificationCell({
  status,
  reasons,
  transactionId,
  thumbnailUrl,
  showReceiptAgentAvatar = false,
}: ReceiptVerificationCellProps): React.ReactElement | null {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()

  if (thumbnailUrl !== undefined) {
    const thumbnail = (
      <span
        css={css`
          width: 32px;
          height: 32px;
          display: block;
          position: relative;
        `}
      >
        <img
          src={thumbnailUrl}
          width={32}
          height={32}
          alt={
            status === ReceiptVerificationStatus.Verified
              ? 'Uploaded and verified receipt'
              : 'Uploaded receipt'
          }
          css={css`
            width: 32px;
            height: 32px;
            display: block;
            object-fit: cover;
            border: 0.4px solid #dadbe0;
            border-radius: 4px;
            box-shadow: 0 0.8px 2px rgba(0, 0, 0, 0.1);
          `}
        />
        {status === ReceiptVerificationStatus.Verified && (
          <span
            aria-hidden="true"
            data-testid="receipt-thumbnail-verification"
            css={css`
              position: absolute;
              right: 0;
              bottom: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 16px;
              height: 16px;
              border: 1.6px solid #ffffff;
              border-radius: 50%;
              background: #23b998;
            `}
          >
            <SvgCheckNew width={10} height={10} stroke="#ffffff" />
          </span>
        )}
      </span>
    )

    return showReceiptAgentAvatar && status !== undefined ? (
      <TooltipIcon
        placement="top"
        icon={
          <span
            aria-label={translate(receiptVerificationStatusToTooltip(status))}
            role="img"
          >
            {thumbnail}
          </span>
        }
        iconTestId="receipt-thumbnail-tooltip"
      >
        <PolicyStatusTooltipContent>
          <AttachmentVerificationTooltip status={status} reasons={reasons} />
        </PolicyStatusTooltipContent>
      </TooltipIcon>
    ) : (
      thumbnail
    )
  }

  if (!status) return null

  const label = translate(receiptVerificationStatusToTooltip(status))

  const handleOpenAttachment = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault()
    event.stopPropagation()
    if (transactionId === undefined) return
    queryClient
      .fetchQuery({
        queryKey: transactionsQueryKeys.detail(transactionId),
        queryFn: async () =>
          (
            await axios.get<TransactionDetailResponse>(
              `/transactions/${transactionId}`,
              requestConfig,
            )
          ).data,
        retry: false,
      })
      .then((response) => {
        const detail = new TransactionDetailModel(response)
        const firstAttachment = detail.receiptAttachments[0]
        if (firstAttachment !== undefined) {
          dispatch(
            createSetIsAttachmentPopupOpen(true, undefined, firstAttachment),
          )
        }
      })
      .catch(() => undefined)
  }

  const icon = (
    <span
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 8px;
        border-radius: 8px;
        &:hover {
          background: ${base_colorSurfaceMutedHover};
        }
      `}
      aria-label={label}
      role="img"
    >
      {receiptVerificationStatusToIcon(status, 16)}
    </span>
  )

  const tooltipIcon = (
    <TooltipIcon
      placement="top"
      icon={icon}
      iconTestId={`receipt-verification-${status.toLowerCase()}`}
    >
      {showReceiptAgentAvatar ? (
        <PolicyStatusTooltipContent>
          <AttachmentVerificationTooltip status={status} reasons={reasons} />
        </PolicyStatusTooltipContent>
      ) : (
        <AttachmentVerificationTooltip status={status} reasons={reasons} />
      )}
    </TooltipIcon>
  )

  return transactionId !== undefined ? (
    <button
      type="button"
      onClick={handleOpenAttachment}
      css={css`
        border: none;
        padding: 0;
        margin: 0;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
      `}
    >
      {tooltipIcon}
    </button>
  ) : (
    <div
      css={css`
        display: flex;
        align-items: center;
      `}
    >
      {tooltipIcon}
    </div>
  )
}
